<template>
  <div class="arc-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="圆弧类型" name="type">
        <div class="arc-type-grid">
          <button
            type="button"
            class="arc-type-button"
            :class="{ active: options.mode === SketchArcCommand.CenterStartEnd }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchArcCommand.CenterStartEnd)"
          >
            <SvgIcon :content="arcCenterIcon" alt="圆心/起点/终点" />
          </button>
          <button
            type="button"
            class="arc-type-button"
            :class="{ active: options.mode === SketchArcCommand.Tangent }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchArcCommand.Tangent)"
          >
            <SvgIcon :content="arcTangentIcon" alt="切线弧" />
          </button>
          <button
            type="button"
            class="arc-type-button"
            :class="{ active: options.mode === SketchArcCommand.ThreePoint }"
            :disabled="!canEdit"
            @click="handleModeChange(SketchArcCommand.ThreePoint)"
          >
            <SvgIcon :content="arcThreePointIcon" alt="3点圆弧" />
          </button>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="现有几何关系" name="relations">
        <SelectionListFrame
          :items="existingRelations"
          selection-behavior="ctrl-multiple"
          list-role="list"
          :visible-rows="4"
          context-menu-enabled
          :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
          :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
          item-key-field="relationId"
          @delete-item="handleDeleteRelationSelection"
          @delete-all="handleDeleteAllRelations"
        >
          <template #item="{ item }">
            <SketchRelationDiagnosticText
              :item="item"
              :label="resolveRelationLabel(item)"
            />
          </template>
        </SelectionListFrame>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="添加几何关系" name="add-relations">
        <div class="add-relations-section">
          <n-space size="small" wrap>
            <n-button
              v-for="relation in availableRelations"
              :key="relation.relationType"
              size="small"
              quaternary
              :disabled="relation.disabled"
              @click="handleAddRelation(relation.relationType)"
            >
              {{ relation.label }}
            </n-button>
          </n-space>
        </div>
      </n-collapse-item>

      <n-collapse-item v-if="showAdvancedSections" title="选项" name="options">
        <n-checkbox
          :checked="constructionChecked"
          :disabled="!canEdit || !isArcSelected"
          @update:checked="handleForConstruction"
        >
          作为构造线
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row">
            <n-text depth="3" class="param-label">Xc</n-text>
            <n-input-number
              :value="center2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Yc</n-text>
            <n-input-number
              :value="center2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('y', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Xs</n-text>
            <n-input-number
              :value="start2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleStartInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Ys</n-text>
            <n-input-number
              :value="start2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleStartInput('y', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Xe</n-text>
            <n-input-number
              :value="end2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleEndInput('x', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">Ye</n-text>
            <n-input-number
              :value="end2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleEndInput('y', value)"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">R</n-text>
            <n-input-number
              :value="radius ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleRadiusInput"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">角度</n-text>
            <n-input-number
              :value="angle ?? null"
              size="small"
              :disabled="true"
              :show-button="false"
              :precision="8"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import {
  NButton,
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NSpace,
  NText,
} from 'naive-ui';
import type { ArcOptions, ArcParameters, ArcParameterUpdate, PropertyManagerContext } from '@/core';
import * as THREE from 'three';
import { useViewer } from '@/hooks';
import { useGetSelectArc } from '@/hooks';
import SelectionListFrame from '@/components/editor/SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import SvgIcon from '@/components/common/SvgIcon.vue';
import {
  type AvailableRelationButtonViewModel,
  roundToEightDecimalPlaces,
  SketchArcCommand,
  type SketchRelationKind,
  type SketchRelationSnapshot,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  //------ 统一单位转换 结束------
} from '@puppycad/cad-core';
import arcCenterIcon from '@/assets/icon/arc_center.svg?raw';
import arcTangentIcon from '@/assets/icon/arc_tangent.svg?raw';
import arcThreePointIcon from '@/assets/icon/arc_3point.svg?raw';

defineOptions({
  inheritAttrs: false,
});

interface ArcPropertySectionProps {
  /** 属性面板上下文，用于统一提交草图属性编辑。 */
  context?: PropertyManagerContext;
}

const props = defineProps<ArcPropertySectionProps>();

const defaultOptions: ArcOptions = {
  mode: SketchArcCommand.CenterStartEnd,
  forConstruction: false,
};

//------ 圆弧属性迁移 开始------
const { getViewer } = useViewer();
const { getSelected: getSelectedArc, onChanged: onArcChanged } = useGetSelectArc();
const localArcOptions = ref<ArcOptions>({ ...defaultOptions });
const localArcParameters = ref<ArcParameters | null>(null);
/** 构造线勾选状态。 */
const constructionCheckedValue = ref(false);

/** 是否有圆弧被选中。 */
const isArcSelected = ref(false);
/** 当前选中圆弧的现有关系快照列表。 */
const existingRelations = ref<SketchRelationSnapshot[]>([]);
/** 当前选中圆弧可创建的几何关系按钮列表。 */
const availableRelations = ref<AvailableRelationButtonViewModel[]>([]);
//------ 圆弧构造线勾选状态同步 开始------
/**
 * 同步“作为构造线”勾选状态。
 * @param arc 当前选中的圆弧对象。
 */
function syncConstructionCheckedState(arc: ReturnType<typeof getSelectedArc>): void {
  if (arc) {
    constructionCheckedValue.value = Boolean(arc.isConstruction);
    return;
  }
  constructionCheckedValue.value = Boolean(localArcOptions.value.forConstruction);
}
//------ 圆弧构造线勾选状态同步 结束------

const arcChangedSub = onArcChanged().subscribe((arc) => {
  isArcSelected.value = arc !== null;
  refreshPanelState(arc);
  // 选中变化时同步圆弧面板参数
  syncArcPanelState(arc);
});
onBeforeUnmount(() => {
  arcChangedSub.unsubscribe();
});

/**
 * 从渲染器同步圆弧面板的选项和参数。
 * @param arc 当前选中的圆弧对象；存在时优先从对象定义回填参数。
 */
function syncArcPanelState(arc: ReturnType<typeof getSelectedArc> = getSelectedArc()): void {
  const renderer = getViewer();
  localArcOptions.value = renderer?.getArcDrawingOptions?.() ?? { ...defaultOptions };
  if (arc) {
    // 选中已有圆弧时必须以对象定义为准，绘制态临时参数可能已被清空。
    syncParametersFromArc(arc);
  } else {
    localArcParameters.value = renderer?.getArcDrawingParameters?.() ?? null;
  }
  syncConstructionCheckedState(arc);
}

/**
 * 从圆弧对象重新读取定义并同步到属性面板。
 * 约束求解可能改变用户刚输入的几何值，因此属性面板必须以对象最终定义为准。
 * @param arc 当前圆弧对象。
 */
function syncParametersFromArc(arc: ReturnType<typeof getSelectedArc>): void {
  if (!arc) {
    localArcParameters.value = null;
    return;
  }
  const definition = arc.getArcDefinition();
  const referencePlane = arc.referencePlane;
  const centerLocal = referencePlane.worldToLocal(definition.centerWorld);
  const startLocal = new THREE.Vector2(
    centerLocal.x + definition.radius * Math.cos(definition.startAngle),
    centerLocal.y + definition.radius * Math.sin(definition.startAngle),
  );
  const endLocal = new THREE.Vector2(
    centerLocal.x + definition.radius * Math.cos(definition.endAngle),
    centerLocal.y + definition.radius * Math.sin(definition.endAngle),
  );
  const startWorld = referencePlane.localToWorld(startLocal);
  const endWorld = referencePlane.localToWorld(endLocal);
  const round = (value: number) => roundToEightDecimalPlaces(value);
  let angleRange = definition.clockwise
    ? definition.startAngle - definition.endAngle
    : definition.endAngle - definition.startAngle;
  if (angleRange < 0) {
    angleRange += Math.PI * 2;
  }
  localArcParameters.value = {
    center: definition.centerWorld.toArray() as [number, number, number],
    center2d: [round(centerLocal.x), round(centerLocal.y)],
    start: startWorld.toArray() as [number, number, number],
    start2d: [round(startLocal.x), round(startLocal.y)],
    end: endWorld.toArray() as [number, number, number],
    end2d: [round(endLocal.x), round(endLocal.y)],
    radius: round(definition.radius),
    angle: round((Math.abs(angleRange) * 180) / Math.PI),
  };
  getViewer()?.setArcDrawingParameters?.(localArcParameters.value);
}

/**
 * 将圆弧属性编辑统一交给 PropertyManagerContext 收口。
 * @param arc 被编辑的圆弧对象。
 */
function finalizeArcPropertyEdit(arc: ReturnType<typeof getSelectedArc>): void {
  if (!arc) {
    return;
  }
  props.context?.finalizeSketchPropertyEdit?.(arc, {
    reason: 'property-edit',
    sourceObjectId: arc.uuid,
  });
}

onMounted(() => {
  const initialArc = getSelectedArc();
  isArcSelected.value = initialArc !== null;
  refreshPanelState(initialArc);
  syncArcPanelState(initialArc);
});
const canEdit = computed(() => Boolean(getViewer()));
const canEditParameters = computed(() => Boolean(isArcSelected.value && getViewer()));
const showAdvancedSections = computed(() => isArcSelected.value);
const options = computed(() => localArcOptions.value);
//------ 统一单位转换 开始------
const center2d = computed(() => {
  const c = localArcParameters.value?.center2d ?? null;
  if (!c) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(c[0])),
    roundToEightDecimalPlaces(convertMToMm(c[1])),
  ];
});
const start2d = computed(() => {
  const s = localArcParameters.value?.start2d ?? null;
  if (!s) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(s[0])),
    roundToEightDecimalPlaces(convertMToMm(s[1])),
  ];
});
const end2d = computed(() => {
  const e = localArcParameters.value?.end2d ?? null;
  if (!e) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(e[0])),
    roundToEightDecimalPlaces(convertMToMm(e[1])),
  ];
});
const radius = computed(() => {
  const r = localArcParameters.value?.radius ?? null;
  if (r === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(r));
});
//------ 统一单位转换 结束------
const angle = computed(() => localArcParameters.value?.angle ?? null);
const constructionChecked = computed(() => constructionCheckedValue.value);

const defaultExpanded = ['type', 'parameters', 'options'];

function updateOptions(patch: Partial<ArcOptions>): void {
  const renderer = getViewer();
  const currentOptions = renderer?.getArcDrawingOptions?.();
  if (!renderer || !currentOptions) {
    return;
  }
  renderer.setArcDrawingOptions?.({ ...currentOptions, ...patch });
  localArcOptions.value = { ...localArcOptions.value, ...patch };
}

function handleModeChange(mode: SketchArcCommand): void {
  updateOptions({ mode });
}

//------ 圆弧现有几何关系 开始------

/**
 * 刷新当前选中圆弧的可添加几何关系列表。
 * @param arc 选中的圆弧对象，传入 null 时清空按钮列表。
 */
function refreshAvailableRelations(arc: ReturnType<typeof getSelectedArc>): void {
  if (!arc) {
    availableRelations.value = [];
    return;
  }

  // 可创建关系由 cad-core 统一计算，避免属性面板重复实现圆弧关系规则。
  availableRelations.value = getViewer()?.sketchRelationCreationService.build(arc) ?? [];
}

/**
 * 刷新圆弧属性面板中的几何关系状态。
 * @param arc 选中的圆弧对象，传入 null 时清空关系展示状态。
 */
function refreshPanelState(arc: ReturnType<typeof getSelectedArc>): void {
  refreshExistingRelations(arc);
  refreshAvailableRelations(arc);
}

/**
 * 刷新当前选中圆弧的现有关系列表。
 * @param arc 选中的圆弧对象（null 表示无选中）。
 */
function refreshExistingRelations(arc: ReturnType<typeof getSelectedArc>): void {
  if (!arc) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = arc.constraints.getRelationSnapshots();
}

/**
 * 将关系快照转为显示标签。
 * @param item 列表项。
 * @returns 显示标签。
 */
function resolveRelationLabel(item: unknown): string {
  const snapshot = item as SketchRelationSnapshot | null;
  if (!snapshot) {
    return '';
  }
  return snapshot.baseDisplayName || snapshot.name || String(snapshot.relationType);
}

async function removeArcRelation(
  arc: NonNullable<ReturnType<typeof getSelectedArc>>,
  relationId: number,
): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(arc, relationId) ?? false;
}

/**
 * 处理右键删除单个关系。
 * @param payload SelectionListFrame delete-item 回调参数。
 */
async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const arc = getSelectedArc();
  if (!arc) {
    return;
  }
  const snapshot = payload.item as SketchRelationSnapshot;
  if (!snapshot || !Number.isFinite(snapshot.id) || !isSketchRelationSnapshotDeleteEnabled(snapshot)) {
    return;
  }
  if (!await removeArcRelation(arc, snapshot.id)) {
    return;
  }
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(arc, snapshot.id);
  refreshPanelState(arc);
}

/**
 * 处理右键删除全部关系。
 */
async function handleDeleteAllRelations(): Promise<void> {
  const arc = getSelectedArc();
  if (!arc) {
    return;
  }
  const viewer = getViewer();
  for (const snapshot of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeArcRelation(arc, snapshot.id)) {
      return;
    }
    viewer?.removeObjectConstraintMarker?.(arc, snapshot.id);
  }
  refreshPanelState(arc);
}

//------ 圆弧现有几何关系 结束------

//------ 圆弧添加几何关系 开始------
/**
 * 处理圆弧属性面板中的添加几何关系操作。
 * @param relationType 需要创建的几何关系类型。
 */
function handleAddRelation(relationType: SketchRelationKind): void {
  const selectedArc = getSelectedArc();
  const viewer = getViewer();

  if (!selectedArc || !viewer) {
    return;
  }

  // 创建动作统一下沉到 cad-core，确保约束绑定和首次求解路径一致。
  const created = viewer.sketchRelationCreationService.createRelation({
    objects: [selectedArc],
    relationKind: relationType,
  });
  if (!created) {
    return;
  }

  refreshPanelState(selectedArc);
  syncParametersFromArc(selectedArc);
}
//------ 圆弧添加几何关系 结束------

function handleForConstruction(value: boolean): void {
  if (isArcSelected.value) {
    const selectedArc = getSelectedArc();
    if (selectedArc) {
      // 直接通过对象 setter 修改构造线状态（setter 内部切换材质）。
      selectedArc.isConstruction = value;
      const viewer = getViewer();
      viewer?.syncObjectBaseVisualState?.(selectedArc);
      if (selectedArc.closedCollection) {
        if (value || !viewer?.shadedSketchContoursEnabled) {
          selectedArc.closedCollection.clearContourDisplay?.();
        } else {
          selectedArc.closedCollection.syncContour?.();
        }
      }
      constructionCheckedValue.value = value;
      finalizeArcPropertyEdit(selectedArc);
      syncParametersFromArc(selectedArc);
      return;
    }
  }
  updateOptions({ forConstruction: value });
  constructionCheckedValue.value = value;
}

function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedArc({ type: 'center', axis, value });
}

function handleStartInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedArc({ type: 'start', axis, value });
}

function handleEndInput(axis: 'x' | 'y', value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedArc({ type: 'end', axis, value });
}

function handleRadiusInput(value: number | null): void {
  if (value === null || Number.isNaN(value)) {
    return;
  }
  updateSelectedArc({ type: 'radius', value });
}

function updateSelectedArc(update: ArcParameterUpdate): void {
  const selectedArc = getSelectedArc();
  if (!selectedArc) {
    return;
  }
  const definition = selectedArc.getArcDefinition();
  const referencePlane = selectedArc.referencePlane;
  const centerLocal = referencePlane.worldToLocal(definition.centerWorld);
  //------ 统一单位转换 开始------
  // 在属性编辑计算中，将本地米级（m）坐标转换为毫米（mm）来进行计算
  const currentCenter2d = localArcParameters.value?.center2d
    ? new THREE.Vector2(convertMToMm(localArcParameters.value.center2d[0]), convertMToMm(localArcParameters.value.center2d[1]))
    : new THREE.Vector2(convertMToMm(centerLocal.x), convertMToMm(centerLocal.y));
  const currentStart2d = localArcParameters.value?.start2d
    ? new THREE.Vector2(convertMToMm(localArcParameters.value.start2d[0]), convertMToMm(localArcParameters.value.start2d[1]))
    : new THREE.Vector2(
        convertMToMm(centerLocal.x + definition.radius * Math.cos(definition.startAngle)),
        convertMToMm(centerLocal.y + definition.radius * Math.sin(definition.startAngle)),
      );
  const currentEnd2d = localArcParameters.value?.end2d
    ? new THREE.Vector2(convertMToMm(localArcParameters.value.end2d[0]), convertMToMm(localArcParameters.value.end2d[1]))
    : new THREE.Vector2(
        convertMToMm(centerLocal.x + definition.radius * Math.cos(definition.endAngle)),
        convertMToMm(centerLocal.y + definition.radius * Math.sin(definition.endAngle)),
      );

  const nextCenter2d = currentCenter2d.clone();
  let nextStart2d = currentStart2d.clone();
  let nextEnd2d = currentEnd2d.clone();
  let nextRadius = convertMToMm(definition.radius);

  if (update.type === 'center') {
    if (update.axis === 'x') {
      nextCenter2d.x = update.value;
    } else {
      nextCenter2d.y = update.value;
    }
    const delta = nextCenter2d.clone().sub(currentCenter2d);
    nextStart2d.add(delta);
    nextEnd2d.add(delta);
  } else if (update.type === 'start') {
    if (update.axis === 'x') {
      nextStart2d.x = update.value;
    } else {
      nextStart2d.y = update.value;
    }
  } else if (update.type === 'end') {
    if (update.axis === 'x') {
      nextEnd2d.x = update.value;
    } else {
      nextEnd2d.y = update.value;
    }
  } else if (update.type === 'radius') {
    nextRadius = update.value;
  }

  const EPS = 1e-6 * 1000; // 毫米级别的精度阈值
  if (!Number.isFinite(nextRadius) || nextRadius <= EPS) {
    return;
  }

  const startVec = nextStart2d.clone().sub(nextCenter2d);
  const endVec = nextEnd2d.clone().sub(nextCenter2d);
  if (startVec.length() < EPS || endVec.length() < EPS) {
    return;
  }

  if (update.type === 'radius') {
    nextStart2d = nextCenter2d.clone().add(startVec.normalize().multiplyScalar(nextRadius));
    nextEnd2d = nextCenter2d.clone().add(endVec.normalize().multiplyScalar(nextRadius));
  } else {
    nextRadius = startVec.length();
    if (!Number.isFinite(nextRadius) || nextRadius <= EPS) {
      return;
    }
    nextStart2d = nextCenter2d.clone().add(startVec.normalize().multiplyScalar(nextRadius));
    if (endVec.length() >= EPS) {
      nextEnd2d = nextCenter2d.clone().add(endVec.normalize().multiplyScalar(nextRadius));
    }
  }

  const nextCenter2dInM = new THREE.Vector2(convertMmToM(nextCenter2d.x), convertMmToM(nextCenter2d.y));
  const newCenterWorld = referencePlane.localToWorld(nextCenter2dInM);

  const startAngle = Math.atan2(nextStart2d.y - nextCenter2d.y, nextStart2d.x - nextCenter2d.x);
  let endAngle = Math.atan2(nextEnd2d.y - nextCenter2d.y, nextEnd2d.x - nextCenter2d.x);
  const clockwise = definition.clockwise;
  if (clockwise) {
    if (endAngle > startAngle) {
      endAngle -= Math.PI * 2;
    }
  } else {
    if (endAngle < startAngle) {
      endAngle += Math.PI * 2;
    }
  }

  selectedArc.updateArcDefinition({
    centerWorld: newCenterWorld,
    radius: convertMmToM(nextRadius),
    startAngle,
    endAngle,
    clockwise,
    segments: definition.segments,
  });
  //------ 统一单位转换 结束------

  finalizeArcPropertyEdit(selectedArc);
  syncParametersFromArc(selectedArc);
}
//------ 圆弧属性迁移 结束------
</script>

<style scoped>
.arc-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.arc-type-grid {
  display: grid;
  grid-template-columns: repeat(3, 48px);
  gap: 8px;
}

.arc-type-button {
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.02);
  border-radius: 4px;
  cursor: pointer;
  padding: 0;
}

.arc-type-button :deep(.puppy-svg-icon) {
  width: 28px;
  height: 28px;
}

.arc-type-button.active {
  border-color: rgba(66, 153, 225, 0.8);
  box-shadow: 0 0 0 1px rgba(66, 153, 225, 0.4) inset;
}

.arc-type-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.add-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 32px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
